/**
 * Box-Configurator-Engine
 * ------------------------
 * Boxtyp-unabhängige Logik, die von JEDEM Verpackungs-Konfigurator im Theme
 * wiederverwendet wird: Formularfelder überwachen, Bestell-Panel öffnen,
 * Kontaktdaten validieren, Bestellung an den Shopify-App-Proxy senden und
 * zum Checkout weiterleiten.
 *
 * Die eigentliche Preis-/Maßkalkulation gehört NICHT hierher, sondern in ein
 * eigenes Modul pro Boxtyp (siehe box-calc-stuelpdeckelbox.js). Diese Datei
 * kennt nur die generische Vertragsform des Ergebnisses: { valid, note, qty,
 * totalPerPiece, total, ... }.
 *
 * Verwendung (siehe box-configurator-stuelpdeckelbox.liquid für ein
 * vollständiges Beispiel):
 *
 *   window.BoxConfiguratorEngine.register({
 *     rootId: 'StuelpdeckelboxCheckout',
 *     boxType: 'stuelpdeckelbox',
 *     productTitle: '...',
 *     productHandle: '...',
 *     productUrl: '...',
 *     currency: 'EUR',
 *     calculate: BoxCalculators.stuelpdeckelbox.calculate,
 *     getInputs: function (root) { ... liest Felder aus root, gibt inputs zurück ... },
 *     onCalculate: function (calc, inputs, root) { ... aktualisiert Preis-/Summary-Anzeige ... },
 *     buildLineItemProperties: function (calc, inputs) { ... gibt {Label: Wert} zurück ... },
 *     validate: function (calc, inputs, root) { ... optional: eigene Zusatzprüfung vor dem
 *       Absenden, z. B. "Datei-Upload ist Pflicht, wenn Veredelung = Ja". Rückgabe: ein
 *       Fehlertext (String) blockiert das Absenden, null/undefined lässt es durch ... }
 *   });
 *
 * Datei-Upload (generisch, optional): Enthält das Markup ein Feld mit
 * [data-box-role="file-upload"] (<input type="file">), wird die ausgewählte
 * Datei automatisch als Base64-Data-URL in den Payload unter `file` gelegt
 * ({ name, type, dataUrl }) – unabhängig vom Boxtyp. Das Backend entscheidet,
 * ob/wie es die Datei speichert (siehe draft-order.php).
 */
(function (window, document) {
  'use strict';

  var DEFAULT_ENDPOINT = '/apps/box-checkout/draft-order';

  // Clientseitige Obergrenze für Datei-Uploads (Original-Dateigröße). Als
  // Base64 kodiert wird die Datei ca. 1,37x größer – Shared-Hosting-PHP hat
  // oft niedrige upload_max_filesize/post_max_size-Limits (All-Inkl-Standard
  // ist meist 32 MB). 10 MB Original ≈ 13,7 MB Base64, sollte überall passen.
  // Bei Bedarf hier UND in draft-order.php ($MAX_FILE_BYTES) anpassen.
  var MAX_FILE_BYTES = 10 * 1024 * 1024;

  // Shopify braucht nach dem Anlegen eines Draft Orders einen kurzen Moment,
  // bis die zurückgelieferte invoice_url tatsächlich abrufbar ist. Navigiert
  // man sofort dorthin, zeigt Shopify manchmal kurz "Invoice is not ready
  // yet" an (behoben durch einmaliges Neuladen der Seite) – ein kurzer,
  // künstlicher Delay vor der Weiterleitung verhindert das zuverlässig.
  var INVOICE_READY_DELAY_MS = 1800;

  function delay(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  var MESSAGES = {
    missingName: 'Bitte geben Sie Ihren Namen ein.',
    invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    genericError: 'Die Bestellung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    missingCheckoutUrl: 'Der Shopify-Checkout-Link fehlt in der Serverantwort.',
    fileTooLarge: 'Die Datei ist zu groß (maximal 10 MB).',
    fileReadError: 'Die Datei konnte nicht gelesen werden. Bitte versuchen Sie es erneut.',
    preparing: 'Checkout wird vorbereitet …'
  };

  function readFileAsDataUrl(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () { resolve(reader.result); };
      reader.onerror = function () { reject(new Error('file-read-error')); };
      reader.readAsDataURL(file);
    });
  }

  function qs(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function setStatus(el, message, isError) {
    if (!el) return;
    el.hidden = !message;
    el.textContent = message || '';
    el.classList.toggle('box-form-message--error', Boolean(isError));
  }

  // Betriebsurlaub-Hinweis (siehe company-notice.js): boxtyp-unabhängig, wird
  // deshalb hier zentral für ALLE Konfiguratoren gerendert statt pro Snippet
  // einzeln. Erscheint automatisch nur innerhalb des dort definierten
  // Zeitraums und verschwindet danach von selbst, ohne dass hier etwas
  // geändert werden muss. Fehlt company-notice.js (z. B. Skript-Tag im
  // Snippet vergessen), wird der Hinweis einfach übersprungen statt einen
  // Fehler zu werfen.
  function renderHolidayNotice(root) {
    if (!window.CompanyNotice || !window.CompanyNotice.isBetriebsurlaub()) return;
    if (root.querySelector('.box-note--holiday')) return; // nicht doppelt einfügen

    var notice = document.createElement('div');
    notice.className = 'box-note box-note--holiday';
    notice.textContent = window.CompanyNotice.NOTICE_TEXT;
    root.insertBefore(notice, root.firstChild);
  }

  function registerConfigurator(config) {
    if (!config || !config.rootId) {
      console.error('[BoxConfiguratorEngine] rootId fehlt in der Konfiguration.');
      return null;
    }

    var root = document.getElementById(config.rootId);
    if (!root) {
      console.error('[BoxConfiguratorEngine] Element #' + config.rootId + ' wurde nicht gefunden.');
      return null;
    }

    renderHolidayNotice(root);

    if (typeof config.calculate !== 'function' || typeof config.getInputs !== 'function' || typeof config.onCalculate !== 'function') {
      console.error('[BoxConfiguratorEngine] calculate / getInputs / onCalculate müssen Funktionen sein.');
      return null;
    }

    var endpoint = config.endpoint || DEFAULT_ENDPOINT;
    var messages = Object.assign({}, MESSAGES, config.messages || {});

    var panel = qs(root, '[data-box-role="panel"]');
    var openPanelBtn = qs(root, '[data-box-role="open-panel"]');
    var submitBtn = qs(root, '[data-box-role="submit"]');
    var statusEl = qs(root, '[data-box-role="status"]');

    var nameInput = qs(root, '[data-box-role="customer-name"]');
    var emailInput = qs(root, '[data-box-role="customer-email"]');
    var companyInput = qs(root, '[data-box-role="customer-company"]');
    var phoneInput = qs(root, '[data-box-role="customer-phone"]');
    var messageInput = qs(root, '[data-box-role="customer-message"]');
    var fileInput = qs(root, '[data-box-role="file-upload"]');

    var submitDefaultLabel = submitBtn ? submitBtn.textContent : '';

    var state = {
      calc: null,
      inputs: null
    };

    function recalc() {
      var inputs = config.getInputs(root);
      var calc = config.calculate(inputs);
      state.inputs = inputs;
      state.calc = calc;
      config.onCalculate(calc, inputs, root);
      return calc;
    }

    // Delegierte Listener: reagiert auf jedes Feld mit [data-box-field]
    // innerhalb des Root-Elements, ohne dass die Engine die einzelnen
    // Feld-IDs kennen muss (boxtyp-unabhängig).
    root.addEventListener('input', function (event) {
      if (event.target.closest('[data-box-field]')) recalc();
    });
    root.addEventListener('change', function (event) {
      if (event.target.closest('[data-box-field]')) recalc();
    });

    if (openPanelBtn && panel) {
      openPanelBtn.addEventListener('click', function () {
        var calc = recalc();
        if (!calc || !calc.valid) return;
        panel.hidden = false;
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    function buildPayload(calc, inputs) {
      var properties = typeof config.buildLineItemProperties === 'function'
        ? config.buildLineItemProperties(calc, inputs)
        : {};

      return {
        boxType: config.boxType,
        productTitle: config.productTitle,
        productHandle: config.productHandle,
        productUrl: config.productUrl,
        currency: config.currency,
        inputs: inputs,
        properties: properties,
        customer: {
          name: nameInput ? nameInput.value.trim() : '',
          email: emailInput ? emailInput.value.trim() : '',
          company: companyInput ? companyInput.value.trim() : '',
          phone: phoneInput ? phoneInput.value.trim() : ''
        },
        message: messageInput ? messageInput.value.trim() : ''
      };
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        var calc = recalc();
        setStatus(statusEl, '', false);

        if (!calc || !calc.valid) {
          setStatus(statusEl, calc ? calc.note : messages.genericError, true);
          return;
        }

        // Name/E-Mail sind optional: nicht jeder Konfigurator fragt sie ab
        // (der Kunde gibt sie ohnehin gleich im Shopify-Checkout ein). Nur
        // validieren, wenn die entsprechenden Felder im Markup vorhanden sind.
        if (nameInput && !nameInput.value.trim()) {
          setStatus(statusEl, messages.missingName, true);
          nameInput.focus();
          return;
        }

        if (emailInput && !emailInput.checkValidity()) {
          setStatus(statusEl, messages.invalidEmail, true);
          emailInput.focus();
          return;
        }

        if (typeof config.validate === 'function') {
          var validationError = config.validate(calc, state.inputs, root);
          if (validationError) {
            setStatus(statusEl, validationError, true);
            return;
          }
        }

        var selectedFile = fileInput && fileInput.files && fileInput.files[0] ? fileInput.files[0] : null;
        if (selectedFile && selectedFile.size > MAX_FILE_BYTES) {
          setStatus(statusEl, messages.fileTooLarge, true);
          fileInput.focus();
          return;
        }

        submitBtn.disabled = true;
        submitBtn.textContent = messages.preparing;

        var filePromise = selectedFile
          ? readFileAsDataUrl(selectedFile).then(function (dataUrl) {
              return { name: selectedFile.name, type: selectedFile.type, dataUrl: dataUrl };
            })
          : Promise.resolve(null);

        filePromise
          .catch(function () {
            throw new Error(messages.fileReadError);
          })
          .then(function (fileData) {
            var payload = buildPayload(calc, state.inputs);
            if (fileData) payload.file = fileData;

            return fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(payload)
            });
          })
          .then(function (response) {
            return response.json().catch(function () { return {}; }).then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok) {
              throw new Error(result.data.error || result.data.message || messages.genericError);
            }

            var checkoutUrl = result.data.invoiceUrl || result.data.invoice_url ||
              result.data.checkoutUrl || result.data.checkout_url;

            if (!checkoutUrl) {
              throw new Error(messages.missingCheckoutUrl);
            }

            // Kurzer Delay, bevor zur Shopify-Rechnungsseite weitergeleitet
            // wird (siehe INVOICE_READY_DELAY_MS oben) – der Button zeigt in
            // dieser Zeit weiterhin "Checkout wird vorbereitet …" an.
            return delay(INVOICE_READY_DELAY_MS).then(function () {
              window.location.assign(checkoutUrl);
            });
          })
          .catch(function (error) {
            setStatus(statusEl, (error && error.message) || messages.genericError, true);
            submitBtn.disabled = false;
            submitBtn.textContent = submitDefaultLabel;
          });
      });
    }

    document.addEventListener('DOMContentLoaded', recalc);
    if (document.readyState !== 'loading') recalc();

    return {
      root: root,
      recalc: recalc,
      getState: function () { return state; }
    };
  }

  window.BoxConfiguratorEngine = {
    register: registerConfigurator
  };
})(window, document);
